import { parameterUtils, type ModelData, type ModelParameter, type HardwareItem } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

const frameThickness = 60

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimension(type: string, value: number): number {
  const rules: Record<string, { min: number; max: number }> = {
    totalWidth: { min: 2500, max: 12000 },
    totalHeight: { min: 1800, max: 8000 },
    rightSectionWidth: { min: 300, max: 2000 },
    doorLeftWidth: { min: 400, max: 2000 },
    doorRightWidth: { min: 400, max: 2000 },
    centerSectionWidth: { min: 600, max: 3000 },
    panelHeight: { min: 200, max: 3000 },
    doorHeight: { min: 1500, max: 7000 },
    transomHeight: { min: 150, max: 800 },
  }
  const rule = rules[type] || { min: 100, max: 10000 }
  return Math.max(rule.min, Math.min(rule.max, value))
}

function shiftComponentsRight(model: ModelData, componentIds: string[], widthDifference: number) {
  componentIds.forEach((componentId) => {
    const component = getParam(model.parameters!, componentId)
    if (component?.position) {
      updateParam(model, componentId, {
        position: {
          ...component.position,
          x: (component.position.x || 0) + widthDifference,
        } as ModelParameter['position'],
      })
    }
  })
}

const RIGHT_SECTION_ELEMENTS = [
  'right_vertical_frame_profile',
  'right_glass_panel_top',
  'right_glass_panel_opanable',
  'right_glass_panel_bottom',
  'right_window_middle_horizontal_frame_profile',
  'right_window_middle_horizontal_frame_profile_2',
  'top_window_1_left_frame_profile',
  'top_window_1_right_frame_profile',
  'top_window_1_top_frame_profile',
  'top_window_1_bottom_frame_profile',
  'bottom_right_frame_profile',
]

function updateRightSectionPanels(
  model: ModelData,
  heightDifference: number,
) {
  const params = model.parameters!
  const rightPanelTop = getParam(params, 'right_glass_panel_top')
  const rightPanelOpenable = getParam(params, 'right_glass_panel_opanable')
  const rightPanelBottom = getParam(params, 'right_glass_panel_bottom')

  const currentPanel1 = rightPanelTop?.height || 613
  const currentPanel2 = rightPanelOpenable?.height || 614
  const currentPanel3 = rightPanelBottom?.height || 613
  const currentTotalPanels = currentPanel1 + currentPanel2 + currentPanel3

  const panel1Ratio = currentPanel1 / currentTotalPanels
  const panel2Ratio = currentPanel2 / currentTotalPanels
  const panel3Ratio = currentPanel3 / currentTotalPanels

  const newPanel1Height = Math.round(currentPanel1 + heightDifference * panel1Ratio)
  const newPanel2Height = Math.round(currentPanel2 + heightDifference * panel2Ratio)
  const newPanel3Height = Math.round(currentPanel3 + heightDifference * panel3Ratio)

  const topMargin = 60

  updateParam(model, 'right_glass_panel_top', {
    height: newPanel1Height,
    position: { x: rightPanelTop?.position?.x || 2960, y: topMargin, z: 0 } as ModelParameter['position'],
  })

  const horizontal1Y = topMargin + newPanel1Height
  const rightWindowHorizontal1 = getParam(params, 'right_window_middle_horizontal_frame_profile')
  updateParam(model, 'right_window_middle_horizontal_frame_profile', {
    position: { x: rightWindowHorizontal1?.position?.x || 2960, y: horizontal1Y, z: 0 } as ModelParameter['position'],
  })

  const panel2Y = horizontal1Y + frameThickness
  const windowFrameHeight = newPanel2Height + 120

  updateParam(model, 'right_glass_panel_opanable', {
    height: newPanel2Height,
    position: { x: rightPanelOpenable?.position?.x || 3020, y: panel2Y + 60, z: 0 } as ModelParameter['position'],
  })

  const handleHeightConst = 131
  const totalHandleY = Math.round(windowFrameHeight / 2 - handleHeightConst / 2)
  const topHingeY = windowFrameHeight * 0.15
  const bottomHingeY = windowFrameHeight * 0.85

  const topWindow1Left = getParam(params, 'top_window_1_left_frame_profile')
  const topWindow1Right = getParam(params, 'top_window_1_right_frame_profile')
  const topWindow1Top = getParam(params, 'top_window_1_top_frame_profile')
  const topWindow1Bottom = getParam(params, 'top_window_1_bottom_frame_profile')

  updateParam(model, 'top_window_1_left_frame_profile', {
    height: windowFrameHeight,
    position: { x: topWindow1Left?.position?.x || 2960, y: panel2Y, z: 0 } as ModelParameter['position'],
    hardware: {
      handles: [{ ...topWindow1Left?.hardware?.handles?.[0], position: { x: -10, y: totalHandleY, z: 10 } } as HardwareItem],
    },
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    height: windowFrameHeight,
    position: { x: topWindow1Right?.position?.x || 3480, y: panel2Y, z: 0 } as ModelParameter['position'],
    hardware: {
      hinges: [
        { ...topWindow1Right?.hardware?.hinges?.[0], position: { x: 60, y: topHingeY, z: 10 } } as HardwareItem,
        { ...topWindow1Right?.hardware?.hinges?.[1], position: { x: 60, y: bottomHingeY, z: 10 } } as HardwareItem,
      ],
    },
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: topWindow1Top?.position?.x || 2960, y: panel2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: { x: topWindow1Bottom?.position?.x || 2960, y: panel2Y + newPanel2Height + 60, z: 0 } as ModelParameter['position'],
  })

  const horizontal2Y = panel2Y + windowFrameHeight
  const rightWindowHorizontal2 = getParam(params, 'right_window_middle_horizontal_frame_profile_2')
  updateParam(model, 'right_window_middle_horizontal_frame_profile_2', {
    position: { x: rightWindowHorizontal2?.position?.x || 2960, y: horizontal2Y, z: 0 } as ModelParameter['position'],
  })

  const panel3Y = horizontal2Y + frameThickness
  updateParam(model, 'right_glass_panel_bottom', {
    height: newPanel3Height,
    position: { x: rightPanelBottom?.position?.x || 2960, y: panel3Y, z: 0 } as ModelParameter['position'],
  })
}

function updateDoorHardware(model: ModelData, newDoorHeight: number) {
  // Door 1 left frame hinges
  const door1LeftFrame = getParam(model.parameters!, 'double_door_1_left_frame_profile')
  if (door1LeftFrame?.hardware?.hinges) {
    const updatedHinges = door1LeftFrame.hardware.hinges.map((hinge) => {
      if (hinge.positionId === 'door_1_left_bottom_hinge_1') {
        return { ...hinge, position: { ...hinge.position, y: Math.round(newDoorHeight * 0.75) } }
      }
      if (hinge.positionId === 'door_1_left_bottom_hinge_2') {
        return { ...hinge, position: { ...hinge.position, y: Math.round(newDoorHeight * 0.85) } }
      }
      return hinge
    })
    updateParam(model, 'double_door_1_left_frame_profile', { hardware: { ...door1LeftFrame.hardware, hinges: updatedHinges } })
  }

  // Door 2 left frame handle
  const door2LeftFrame = getParam(model.parameters!, 'double_door_2_left_frame_profile')
  if (door2LeftFrame?.hardware?.handles) {
    const updatedHandles = door2LeftFrame.hardware.handles.map((handle) => {
      if (handle.positionId === 'door_2_left_handle_1') {
        return { ...handle, position: { ...handle.position, y: Math.round(newDoorHeight * 0.55) } }
      }
      return handle
    })
    updateParam(model, 'double_door_2_left_frame_profile', { hardware: { ...door2LeftFrame.hardware, handles: updatedHandles } })
  }

  // Door 2 right frame hinges
  const door2RightFrame = getParam(model.parameters!, 'double_door_2_right_frame_profile')
  if (door2RightFrame?.hardware?.hinges) {
    const updatedHinges = door2RightFrame.hardware.hinges.map((hinge) => {
      if (hinge.positionId === 'door_2_right_bottom_hinge_1') {
        return { ...hinge, position: { ...hinge.position, y: Math.round(newDoorHeight * 0.75) } }
      }
      if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
        return { ...hinge, position: { ...hinge.position, y: Math.round(newDoorHeight * 0.85) } }
      }
      return hinge
    })
    updateParam(model, 'double_door_2_right_frame_profile', { hardware: { ...door2RightFrame.hardware, hinges: updatedHinges } })
  }
}

/**
 * Additive height for right section panels (top/openable/bottom).
 * Total height stays constant; panels redistribute.
 */
function handleAdditiveHeightUpdate(model: ModelData, updateType: string, newHeight: number) {
  const params = model.parameters!
  const panel1 = getParam(params, 'right_glass_panel_top')
  const panel2 = getParam(params, 'right_glass_panel_opanable')
  const panel3 = getParam(params, 'right_glass_panel_bottom')
  const horizontal1 = getParam(params, 'right_window_middle_horizontal_frame_profile')
  const horizontal2 = getParam(params, 'right_window_middle_horizontal_frame_profile_2')

  if (!panel1 || !panel2 || !panel3 || !horizontal1 || !horizontal2) return

  const topMargin = 60
  let newPanel1Height: number
  let newPanel2Height: number
  let newPanel3Height: number

  switch (updateType) {
    case 'leftPanel1Height': {
      const heightDifference = newHeight - (panel1.height || 0)
      const currentPanel2And3 = (panel2.height || 0) + (panel3.height || 0)
      const panel2Ratio = (panel2.height || 0) / currentPanel2And3
      const panel3Ratio = (panel3.height || 0) / currentPanel2And3
      newPanel1Height = newHeight
      newPanel2Height = Math.round((panel2.height || 0) - heightDifference * panel2Ratio)
      newPanel3Height = Math.round((panel3.height || 0) - heightDifference * panel3Ratio)
      break
    }
    case 'leftPanel2Height': {
      const heightDifference = newHeight - (panel2.height || 0)
      const currentPanel1And3 = (panel1.height || 0) + (panel3.height || 0)
      const panel1Ratio = (panel1.height || 0) / currentPanel1And3
      const panel3Ratio = (panel3.height || 0) / currentPanel1And3
      newPanel1Height = Math.round((panel1.height || 0) - heightDifference * panel1Ratio)
      newPanel2Height = newHeight
      newPanel3Height = Math.round((panel3.height || 0) - heightDifference * panel3Ratio)
      break
    }
    case 'leftPanel3Height': {
      const heightDifference = newHeight - (panel3.height || 0)
      const currentPanel1And2 = (panel1.height || 0) + (panel2.height || 0)
      const panel1Ratio = (panel1.height || 0) / currentPanel1And2
      const panel2Ratio = (panel2.height || 0) / currentPanel1And2
      newPanel1Height = Math.round((panel1.height || 0) - heightDifference * panel1Ratio)
      newPanel2Height = Math.round((panel2.height || 0) - heightDifference * panel2Ratio)
      newPanel3Height = newHeight
      break
    }
    default:
      return
  }

  updateParam(model, 'right_glass_panel_top', { height: newPanel1Height })

  const newHorizontal1Y = topMargin + newPanel1Height
  updateParam(model, 'right_window_middle_horizontal_frame_profile', {
    position: { x: horizontal1.position.x, y: newHorizontal1Y, z: 0 } as ModelParameter['position'],
  })

  const newPanel2Y = newHorizontal1Y + frameThickness
  const newWindowFrameHeight = newPanel2Height + 120

  updateParam(model, 'right_glass_panel_opanable', {
    height: newPanel2Height,
    position: { x: panel2.position.x, y: newPanel2Y + 60, z: 0 } as ModelParameter['position'],
  })

  const handleHeight = 131
  const newHandleY = newWindowFrameHeight / 2 - handleHeight / 2

  const topWindow1Left = getParam(params, 'top_window_1_left_frame_profile')
  const topWindow1Right = getParam(params, 'top_window_1_right_frame_profile')
  const topWindow1Top = getParam(params, 'top_window_1_top_frame_profile')
  const topWindow1Bottom = getParam(params, 'top_window_1_bottom_frame_profile')

  updateParam(model, 'top_window_1_left_frame_profile', {
    height: newWindowFrameHeight,
    position: { x: topWindow1Left?.position?.x || 0, y: newPanel2Y, z: 0 } as ModelParameter['position'],
    hardware: { handles: [{ ...topWindow1Left?.hardware?.handles?.[0], position: { x: -10, y: newHandleY, z: 10 } } as HardwareItem] },
  })

  const topHingeY = newWindowFrameHeight * 0.15
  const bottomHingeY = newWindowFrameHeight * 0.85
  updateParam(model, 'top_window_1_right_frame_profile', {
    height: newWindowFrameHeight,
    position: { x: topWindow1Right?.position?.x || 0, y: newPanel2Y, z: 0 } as ModelParameter['position'],
    hardware: {
      hinges: [
        { ...topWindow1Right?.hardware?.hinges?.[0], position: { x: 60, y: topHingeY, z: 10 } } as HardwareItem,
        { ...topWindow1Right?.hardware?.hinges?.[1], position: { x: 60, y: bottomHingeY, z: 10 } } as HardwareItem,
      ],
    },
  })

  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: topWindow1Top?.position?.x || 0, y: newPanel2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: { x: topWindow1Bottom?.position?.x || 0, y: newPanel2Y + newPanel2Height + 60, z: 0 } as ModelParameter['position'],
  })

  const newHorizontal2Y = newPanel2Y + newWindowFrameHeight
  updateParam(model, 'right_window_middle_horizontal_frame_profile_2', {
    position: { x: horizontal2.position.x, y: newHorizontal2Y, z: 0 } as ModelParameter['position'],
  })

  const newPanel3Y = newHorizontal2Y + frameThickness
  updateParam(model, 'right_glass_panel_bottom', {
    height: newPanel3Height,
    position: { x: panel3.position.x, y: newPanel3Y, z: 0 } as ModelParameter['position'],
  })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalWidth', numValue)
  const topFrameWidth = getParam(model.parameters!, 'top_frame_profile')?.width || 3600

  model.width = validatedValue
  updateParam(model, 'top_frame_profile', { width: validatedValue })
  updateParam(model, 'right_frame_profile', { position: { x: validatedValue - 60, y: 0, z: 0 } as ModelParameter['position'] })

  if (!cascadeUpdates) return model

  const widthDifference = validatedValue - topFrameWidth
  const centerGlass = getParam(model.parameters!, 'center_glass_panel')
  const bottomCenterProfile = getParam(model.parameters!, 'bottom_center_frame_profile')

  updateParam(model, 'center_glass_panel', { width: (centerGlass?.width || 1280) + widthDifference })
  updateParam(model, 'bottom_center_frame_profile', { width: (bottomCenterProfile?.width || 1280) + widthDifference })

  shiftComponentsRight(model, RIGHT_SECTION_ELEMENTS, widthDifference)

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalHeight', numValue)
  const currentTotalHeight = model.height as number
  const heightDifference = validatedValue - currentTotalHeight

  model.height = validatedValue
  updateParam(model, 'left_frame_profile', { height: validatedValue })
  updateParam(model, 'right_frame_profile', { height: validatedValue })

  if (!cascadeUpdates) return model

  updateParam(model, 'center_glass_panel', { height: validatedValue - 120 })

  const transomPanel = getParam(model.parameters!, 'double_door_top_glass_panel')
  const transomHeight = transomPanel?.height || 200
  const doubleDoorMiddleHorizontal = getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')
  const transomSectionHeight = 60 + transomHeight + (doubleDoorMiddleHorizontal?.height || 60)

  const newDoorHeight = validatedValue - transomSectionHeight
  const newDoorGlassHeight = newDoorHeight - 120

  updateParam(model, 'double_door_glass_panel_1', { height: newDoorGlassHeight })
  updateParam(model, 'double_door_glass_panel_2', { height: newDoorGlassHeight })

  updateParam(model, 'double_door_1_left_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_1_right_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_middle_vertical_frame_profile_center', { height: newDoorHeight })
  updateParam(model, 'double_door_2_left_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_2_right_frame_profile', { height: newDoorHeight })

  updateDoorHardware(model, newDoorHeight)

  updateParam(model, 'middle_vertical_frame_profile_center', { height: validatedValue - 60 })
  updateParam(model, 'right_vertical_frame_profile', { height: validatedValue - 60 })

  updateRightSectionPanels(model, heightDifference)

  const bottomY = validatedValue - 60
  updateParam(model, 'double_door_bottom_frame_profile_1', {
    position: { x: getParam(model.parameters!, 'double_door_bottom_frame_profile_1')?.position?.x || 60, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'double_door_bottom_frame_profile_2', {
    position: { x: getParam(model.parameters!, 'double_door_bottom_frame_profile_2')?.position?.x || 840, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_center_frame_profile', {
    position: { x: getParam(model.parameters!, 'bottom_center_frame_profile')?.position?.x || 1680, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: getParam(model.parameters!, 'bottom_right_frame_profile')?.position?.x || 2900, y: bottomY, z: 0 } as ModelParameter['position'],
  })

  return model
}

const leftSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newRightFrameWidth = numValue
  const newRightGlassWidth = newRightFrameWidth - 80

  updateParam(model, 'bottom_right_frame_profile', { width: newRightFrameWidth })
  updateParam(model, 'right_glass_panel_1', { width: newRightGlassWidth })
  updateParam(model, 'right_glass_panel_2', { width: newRightGlassWidth })
  updateParam(model, 'right_glass_panel_3', { width: newRightGlassWidth })
  updateParam(model, 'middle_horizontal_frame_profile_1', { width: newRightGlassWidth })
  updateParam(model, 'middle_horizontal_frame_profile_2', { width: newRightGlassWidth })

  if (!cascadeUpdates) return model

  // Recalculate total width and positions
  const doorFrameWidth1 = getParam(model.parameters!, 'bottom_door1_frame_profile')?.width || 810
  const doorFrameWidth2 = getParam(model.parameters!, 'bottom_door2_frame_profile')?.width || 810
  const centerWidth = getParam(model.parameters!, 'center_glass_panel')?.width || 1240

  const newTotalWidth = 40 + doorFrameWidth1 + 40 + doorFrameWidth2 + 40 + centerWidth + 40 + newRightFrameWidth + 40
  model.width = newTotalWidth

  updateParam(model, 'top_frame_profile', { width: newTotalWidth })
  updateParam(model, 'right_frame_profile', { position: { x: newTotalWidth - 40, y: 0, z: 0 } as ModelParameter['position'] })

  return model
}

const doorLeftWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const currentBottomFrame1 = getParam(model.parameters!, 'double_door_bottom_frame_profile_1')
  const middleVertical = getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')

  const currentFullSectionWidth = (leftFrame?.width || 0) + (currentBottomFrame1?.width || 0) + (middleVertical?.width || 0) / 2
  const widthDifference = numValue - currentFullSectionWidth
  const newBottomFrame1Width = (currentBottomFrame1?.width || 0) + widthDifference
  const newDoor1GlassWidth = newBottomFrame1Width - 120

  updateParam(model, 'double_door_glass_panel_1', { width: newDoor1GlassWidth })
  updateParam(model, 'double_door_bottom_frame_profile_1', { width: newBottomFrame1Width })
  updateParam(model, 'double_door_1_top_frame_profile', { width: newBottomFrame1Width })

  // Shift door1 right frame
  const door1RightFrame = getParam(model.parameters!, 'double_door_1_right_frame_profile')
  if (door1RightFrame?.position) {
    updateParam(model, 'double_door_1_right_frame_profile', {
      position: { ...door1RightFrame.position, x: (door1RightFrame.position.x || 0) + widthDifference } as ModelParameter['position'],
    })
  }

  // Shift everything to the right
  const elementsToShift = [
    'double_door_middle_vertical_frame_profile_center',
    'double_door_2_left_frame_profile',
    'double_door_glass_panel_2',
    'double_door_2_right_frame_profile',
    'double_door_2_top_frame_profile',
    'double_door_bottom_frame_profile_2',
    'middle_vertical_frame_profile_center',
    'center_glass_panel',
    'bottom_center_frame_profile',
    ...RIGHT_SECTION_ELEMENTS,
  ]
  shiftComponentsRight(model, elementsToShift, widthDifference)

  // Update transom widths
  const transomGlass = getParam(model.parameters!, 'double_door_top_glass_panel')
  const middleHorizontal = getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')
  updateParam(model, 'double_door_top_glass_panel', { width: (transomGlass?.width || 0) + widthDifference })
  updateParam(model, 'double_door_middle_horizontal_frame_profile', { width: (middleHorizontal?.width || 0) + widthDifference })

  if (cascadeUpdates) {
    const topFrame = getParam(model.parameters!, 'top_frame_profile')
    const newTotalWidth = (topFrame?.width || 0) + widthDifference
    model.width = newTotalWidth
    updateParam(model, 'top_frame_profile', { width: newTotalWidth })
    updateParam(model, 'right_frame_profile', { position: { x: newTotalWidth - 60, y: 0, z: 0 } as ModelParameter['position'] })
  }

  return model
}

const doorRightWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const doubleDoorMiddleVertical = getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')
  const middleVerticalCenter = getParam(model.parameters!, 'middle_vertical_frame_profile_center')
  const doubleDoorVerticalHalfWidth = (doubleDoorMiddleVertical?.width || 0) / 2
  const middleVerticalCenterWidth = middleVerticalCenter?.width || 60

  const currentBottomFrame2 = getParam(model.parameters!, 'double_door_bottom_frame_profile_2')
  const currentFullSectionWidth = doubleDoorVerticalHalfWidth + (currentBottomFrame2?.width || 0) + middleVerticalCenterWidth
  const widthDifference = numValue - currentFullSectionWidth
  const newBottomFrame2Width = (currentBottomFrame2?.width || 0) + (numValue - currentFullSectionWidth)
  const newDoor2GlassWidth = newBottomFrame2Width - 120

  updateParam(model, 'double_door_glass_panel_2', { width: newDoor2GlassWidth })
  updateParam(model, 'double_door_bottom_frame_profile_2', { width: newBottomFrame2Width })
  updateParam(model, 'double_door_2_top_frame_profile', { width: newBottomFrame2Width })

  const door2RightFrame = getParam(model.parameters!, 'double_door_2_right_frame_profile')
  if (door2RightFrame?.position) {
    updateParam(model, 'double_door_2_right_frame_profile', {
      position: { ...door2RightFrame.position, x: (door2RightFrame.position.x || 0) + widthDifference } as ModelParameter['position'],
    })
  }

  const elementsToShift = [
    'middle_vertical_frame_profile_center',
    'center_glass_panel',
    'bottom_center_frame_profile',
    ...RIGHT_SECTION_ELEMENTS,
  ]
  shiftComponentsRight(model, elementsToShift, widthDifference)

  const transomGlass = getParam(model.parameters!, 'double_door_top_glass_panel')
  const middleHorizontal = getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')
  updateParam(model, 'double_door_top_glass_panel', { width: (transomGlass?.width || 0) + widthDifference })
  updateParam(model, 'double_door_middle_horizontal_frame_profile', { width: (middleHorizontal?.width || 0) + widthDifference })

  if (cascadeUpdates) {
    const topFrame = getParam(model.parameters!, 'top_frame_profile')
    const newTotalWidth = (topFrame?.width || 0) + widthDifference
    model.width = newTotalWidth
    updateParam(model, 'top_frame_profile', { width: newTotalWidth })
    updateParam(model, 'right_frame_profile', { position: { x: newTotalWidth - 60, y: 0, z: 0 } as ModelParameter['position'] })

    const totalModelHeight = getParam(model.parameters!, 'left_frame_profile')?.height || 3600
    const topFrameHeight = getParam(model.parameters!, 'top_frame_profile')?.height || 60
    updateParam(model, 'middle_vertical_frame_profile_center', { height: totalModelHeight - topFrameHeight })
    updateParam(model, 'right_vertical_frame_profile', { height: totalModelHeight - topFrameHeight })
  }

  return model
}

const centerSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const centerGlassPanel = getParam(model.parameters!, 'center_glass_panel')
  const widthDifference = numValue - (centerGlassPanel?.width || 0)

  updateParam(model, 'center_glass_panel', { width: numValue })
  updateParam(model, 'bottom_center_frame_profile', { width: numValue })

  if (!cascadeUpdates) return model

  shiftComponentsRight(model, RIGHT_SECTION_ELEMENTS, widthDifference)

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const newTotalWidth = (topFrame?.width || 0) + widthDifference
  model.width = newTotalWidth
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })
  updateParam(model, 'right_frame_profile', { position: { x: newTotalWidth - 60, y: 0, z: 0 } as ModelParameter['position'] })

  return model
}

const rightSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const bottomRightFrame = getParam(model.parameters!, 'bottom_right_frame_profile')
  const widthDifference = numValue - (bottomRightFrame?.width || 0)

  updateParam(model, 'bottom_right_frame_profile', { width: numValue })

  if (!cascadeUpdates) return model

  const newGlassWidth = numValue - 120
  updateParam(model, 'right_glass_panel_top', { width: newGlassWidth })
  updateParam(model, 'right_glass_panel_bottom', { width: newGlassWidth })

  const newOpenableGlassWidth = newGlassWidth - 120
  updateParam(model, 'right_glass_panel_opanable', { width: newOpenableGlassWidth })

  updateParam(model, 'top_window_1_top_frame_profile', { width: newGlassWidth })
  updateParam(model, 'top_window_1_bottom_frame_profile', { width: newGlassWidth })

  const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
  const topWindow1RightFrame = getParam(model.parameters!, 'top_window_1_right_frame_profile')
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { ...topWindow1RightFrame?.position, x: (rightVerticalFrame?.position?.x || 0) + newGlassWidth } as ModelParameter['position'],
  })

  updateParam(model, 'right_window_middle_horizontal_frame_profile', { width: newGlassWidth })
  updateParam(model, 'right_window_middle_horizontal_frame_profile_2', { width: newGlassWidth })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const newTotalWidth = (topFrame?.width || 0) + widthDifference
  model.width = newTotalWidth
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })
  updateParam(model, 'right_frame_profile', { position: { x: newTotalWidth - 60, y: 0, z: 0 } as ModelParameter['position'] })

  return model
}

const leftSectionHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newDoorHeight = validateDimension('doorHeight', numValue)

  const transomPanel = getParam(model.parameters!, 'double_door_top_glass_panel')
  const transomHeight = transomPanel?.height || 200
  const doubleDoorMiddleHorizontal = getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')
  const transomSectionHeight = 60 + transomHeight + (doubleDoorMiddleHorizontal?.height || 60)

  const newTotalHeight = transomSectionHeight + newDoorHeight
  const currentTotalHeight = model.height as number
  const heightDifference = newTotalHeight - currentTotalHeight

  model.height = Math.max(1800, newTotalHeight)

  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const topFrameHeight = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  updateParam(model, 'middle_vertical_frame_profile_center', { height: newTotalHeight - topFrameHeight })
  updateParam(model, 'right_vertical_frame_profile', { height: newTotalHeight - topFrameHeight })
  updateParam(model, 'center_glass_panel', { height: newTotalHeight - 120 })

  const newDoorGlassHeight = newDoorHeight - 120
  updateParam(model, 'double_door_glass_panel_1', { height: newDoorGlassHeight })
  updateParam(model, 'double_door_glass_panel_2', { height: newDoorGlassHeight })

  updateParam(model, 'double_door_1_left_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_1_right_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_middle_vertical_frame_profile_center', { height: newDoorHeight })
  updateParam(model, 'double_door_2_left_frame_profile', { height: newDoorHeight })
  updateParam(model, 'double_door_2_right_frame_profile', { height: newDoorHeight })

  updateDoorHardware(model, newDoorHeight)

  if (cascadeUpdates) {
    updateRightSectionPanels(model, heightDifference)

    const bottomY = newTotalHeight - 60
    const bottomDoor1Frame = getParam(model.parameters!, 'double_door_bottom_frame_profile_1')
    const bottomDoor2Frame = getParam(model.parameters!, 'double_door_bottom_frame_profile_2')
    updateParam(model, 'double_door_bottom_frame_profile_1', { position: { ...bottomDoor1Frame?.position, y: bottomY } as ModelParameter['position'] })
    updateParam(model, 'double_door_bottom_frame_profile_2', { position: { ...bottomDoor2Frame?.position, y: bottomY } as ModelParameter['position'] })
    updateParam(model, 'bottom_center_frame_profile', {
      position: { x: getParam(model.parameters!, 'bottom_center_frame_profile')?.position?.x || 1680, y: bottomY, z: 0 } as ModelParameter['position'],
    })
    updateParam(model, 'bottom_right_frame_profile', {
      position: { x: getParam(model.parameters!, 'bottom_right_frame_profile')?.position?.x || 2900, y: bottomY, z: 0 } as ModelParameter['position'],
    })
  }

  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newTransomHeight = validateDimension('transomHeight', numValue)
  const currentTotalHeight = getParam(model.parameters!, 'left_frame_profile')?.height || 3600

  const topFrameHeight = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  const horizontalFrameHeight = getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')?.height || 60
  const currentGlassPanelHeight = getParam(model.parameters!, 'double_door_top_glass_panel')?.height || 200
  const currentTransomSectionHeight = topFrameHeight + currentGlassPanelHeight + horizontalFrameHeight

  const newGlassPanelHeight = newTransomHeight - topFrameHeight - horizontalFrameHeight
  const heightDifference = newTransomHeight - currentTransomSectionHeight

  updateParam(model, 'double_door_top_glass_panel', { height: newGlassPanelHeight })

  if (!cascadeUpdates) return model

  const newTotalHeight = currentTotalHeight + heightDifference
  model.height = newTotalHeight

  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })
  updateParam(model, 'center_glass_panel', {
    height: (getParam(model.parameters!, 'center_glass_panel')?.height || 0) + heightDifference,
  })

  updateParam(model, 'right_vertical_frame_profile', { height: newTotalHeight - topFrameHeight })
  updateParam(model, 'middle_vertical_frame_profile_center', { height: newTotalHeight - topFrameHeight })

  // Shift door components down by heightDifference
  const doorShiftElements = [
    'double_door_glass_panel_1', 'double_door_glass_panel_2',
    'double_door_1_top_frame_profile', 'double_door_2_top_frame_profile',
    'double_door_middle_horizontal_frame_profile',
    'double_door_1_left_frame_profile', 'double_door_1_right_frame_profile',
    'double_door_2_left_frame_profile', 'double_door_2_right_frame_profile',
    'double_door_middle_vertical_frame_profile_center',
    'double_door_bottom_frame_profile_1', 'double_door_bottom_frame_profile_2',
  ]
  doorShiftElements.forEach((id) => {
    const el = getParam(model.parameters!, id)
    if (el?.position) {
      updateParam(model, id, { position: { ...el.position, y: (el.position.y || 0) + heightDifference } as ModelParameter['position'] })
    }
  })

  // Update bottom frames
  const bottomY = newTotalHeight - 60
  updateParam(model, 'bottom_center_frame_profile', {
    position: { x: getParam(model.parameters!, 'bottom_center_frame_profile')?.position?.x || 1680, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: getParam(model.parameters!, 'bottom_right_frame_profile')?.position?.x || 2900, y: bottomY, z: 0 } as ModelParameter['position'],
  })

  updateRightSectionPanels(model, heightDifference)

  return model
}

const leftPanel1Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel1Height', validatedValue)
  return model
}

const leftPanel2Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel2Height', validatedValue)
  return model
}

const leftPanel3Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel3Height', validatedValue)
  return model
}

export const model18DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  leftSectionWidth,
  doorLeftWidth,
  doorRightWidth,
  centerSectionWidth,
  rightSectionWidth,
  leftSectionHeight,
  transomHeight,
  leftPanel1Height,
  leftPanel2Height,
  leftPanel3Height,
}
